import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from './Cart';
import productService from '../services/productService';
import flashOfferService from '../services/flashOfferService';
import RelatedFlashOffers from './RelatedFlashOffers';
import { offerRestrictionService } from '../services/membershipService';
import reviewService from '../services/reviewService';
import { useToast } from './Toast';
import SimilarProducts from './SimilarProducts';

// État simulé de l'utilisateur (à remplacer par un vrai système d'auth)
export const useAuth = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Vérifier s'il y a un user sauvegardé dans localStorage
    const savedUser = localStorage.getItem('inza_user');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      // Supprimer l'utilisateur test s'il existe
      if (parsedUser.email === 'test@inza-store.com') {
        localStorage.removeItem('inza_user');
        setUser(null);
      } else {
        setUser(parsedUser);
      }
    }
  }, []);

  return { user, setUser };
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #0b1120;
  color: white;
  padding: 1.5rem;
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  margin-top: 2rem;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.6);
  color: #e5e7eb;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  cursor: pointer;
  margin-bottom: 1.5rem;

  &:hover {
    background: rgba(148, 163, 184, 0.15);
  }
`;

const ProductHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: clamp(2rem, 4vw, 2.75rem);
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  p {
    color: #9ca3af;
  }
`;

const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ProductSummary = styled.div`
  background: rgba(15, 23, 42, 0.85);
  border-radius: 1.25rem;
  padding: 2rem;
  border: 1px solid rgba(56, 189, 248, 0.5);
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
    color: white;
  }
  
  .price {
    font-size: 2rem;
    font-weight: 700;
    color: #38bdf8;
    margin-bottom: 1rem;
  }
  
  .rating {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.8);
    
    .stars {
      color: #fbbf24;
    }
  }
  
  .availability {
    color: #10b981;
    font-weight: 500;
    margin-bottom: 1.5rem;
  }
`;

const ProductCard = styled.div`
  background: rgba(15, 23, 42, 0.9);
  border-radius: 1.25rem;
  padding: 2rem;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.5);

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }

  .price {
    font-size: 2rem;
    font-weight: 700;
    color: #38bdf8;
    margin-bottom: 1rem;
  }

  .subtext {
    font-size: 0.9rem;
    color: #9ca3af;
    margin-bottom: 1.5rem;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SpecItem = styled.div`
  font-size: 0.9rem;

  span {
    color: #9ca3af;
  }
`;

const BuyPanel = styled.div`
  background: rgba(15, 23, 42, 0.85);
  border-radius: 1.25rem;
  padding: 2rem;
  border: 1px solid rgba(56, 189, 248, 0.5);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  p {
    font-size: 0.9rem;
    color: #9ca3af;
  }
`;

const BuyButton = styled.button`
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 9999px;
  padding: 0.9rem 2.5rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background: #0284c7;
    transform: scale(1.02);
  }
`;

const FlashOfferBanner = styled.div`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(245, 158, 11, 0.2));
  border: 2px solid #ef4444;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: 'FLASH DEAL';
    position: absolute;
    top: -0.5rem;
    right: 1rem;
    background: linear-gradient(135deg, #ef4444, #f59e0b);
    color: white;
    padding: 0.25rem 1rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

const FlashOfferTitle = styled.h3`
  color: #f59e0b;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const FlashOfferDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FlashOfferItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  
  &::before {
    content: '✓';
    color: #10b981;
    font-weight: bold;
  }
`;

const FlashOfferPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  
  .original {
    text-decoration: line-through;
    color: #9ca3af;
    font-size: 1.1rem;
  }
  
  .discounted {
    color: #10b981;
    font-size: 1.8rem;
    font-weight: 700;
  }
  
  .discount {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 600;
  }
`;

const AddToCartButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ConfigOption = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid transparent;
  border-radius: 0.5rem;
  padding: 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  &.selected {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.2);
  }
  
  .option-label {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.25rem;
  }
  
  .option-value {
    font-weight: 600;
    color: white;
  }
  
  .price-diff {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #10b981;
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 9999px;
    
    &.premium {
      background: #f59e0b;
    }
  }
`;

const ConfigurationSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: white;
  }
  
  .config-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
    
    .config-label {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
  }
`;

const LoginRequiredBanner = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2));
  border: 2px solid #3b82f6;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: center;
  
  h3 {
    color: #3b82f6;
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  
  p {
    color: #e5e7eb;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }
  
  .buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    
    @media (max-width: 640px) {
      flex-direction: column;
    }
  }
  
  .btn {
    padding: 0.75rem 2rem;
    border-radius: 9999px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    font-size: 0.9rem;
  }
  
  .btn-primary {
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
      transform: scale(1.02);
    }
  }
  
  .btn-secondary {
    background: transparent;
    color: #e5e7eb;
    border: 1px solid rgba(229, 231, 235, 0.3);
    
    &:hover {
      background: rgba(229, 231, 235, 0.1);
    }
  }
`;

const WhatsAppButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #25d366;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background: #128c7e;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(37, 211, 102, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: zoom-out;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  .modal-image {
    max-width: 90%;
    max-height: 90%;
    object-fit: contain;
    border-radius: 0.5rem;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
    animation: zoomIn 0.3s ease;
    
    @keyframes zoomIn {
      from { transform: scale(0.8); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
  }
  
  .close-button {
    position: absolute;
    top: 2rem;
    right: 2rem;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1.5rem;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.1);
    }
  }
  
  .image-info {
    position: absolute;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    text-align: center;
    background: rgba(0, 0, 0, 0.7);
    padding: 1rem 2rem;
    border-radius: 2rem;
    backdrop-filter: blur(10px);
    
    .image-title {
      font-size: 1.2rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
    
    .image-counter {
      font-size: 0.9rem;
      opacity: 0.8;
    }
  }
  
  .nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1.2rem;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-50%) scale(1.1);
    }
    
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
      
      &:hover {
        transform: translateY(-50%);
      }
    }
    
    &.prev {
      left: 2rem;
    }
    
    &.next {
      right: 2rem;
    }
  }
`;

const ProductGallery = styled.div`
  .main-image {
    width: 100%;
    height: 400px;
    border-radius: 1rem;
    overflow: hidden;
    margin-bottom: 1rem;
    cursor: zoom-in;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: scale(1.02);
    }
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .thumbnail-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    
    .thumbnail {
      width: 100%;
      height: 80px;
      border-radius: 0.5rem;
      overflow: hidden;
      cursor: pointer;
      border: 2px solid transparent;
      transition: all 0.2s ease;
      
      &:hover {
        border-color: #3b82f6;
      }
      
      &.active {
        border-color: #3b82f6;
      }
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }
`;

const TabsContainer = styled.div`
  margin-top: 2rem;
  
  .tabs-header {
    display: flex;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 2rem;
    
    .tab-button {
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      padding: 1rem 1.5rem;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      position: relative;
      transition: all 0.3s ease;
      
      &:hover {
        color: white;
      }
      
      &.active {
        color: #3b82f6;
        
        &::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 2px;
          background: #3b82f6;
        }
      }
    }
  }
  
  .tab-content {
    animation: fadeIn 0.3s ease;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const DescriptionSection = styled.div`
  color: white;
  
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #3b82f6;
  }
  
  p {
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1.5rem;
  }
  
  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    margin-top: 2rem;
    
    .feature-item {
      background: rgba(255, 255, 255, 0.05);
      padding: 1rem;
      border-radius: 0.5rem;
      border: 1px solid rgba(255, 255, 255, 0.1);
      
      .feature-icon {
        color: #3b82f6;
        margin-bottom: 0.5rem;
      }
      
      .feature-title {
        font-weight: 600;
        margin-bottom: 0.25rem;
      }
      
      .feature-desc {
        font-size: 0.9rem;
        color: rgba(255, 255, 255, 0.6);
      }
    }
  }
`;

const SpecsTable = styled.div`
  margin-top: 2rem;
  
  table {
    width: 100%;
    border-collapse: collapse;
    
    th, td {
      padding: 1rem;
      text-align: left;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    th {
      color: rgba(255, 255, 255, 0.6);
      font-weight: 500;
      width: 30%;
    }
    
    td {
      color: white;
      font-weight: 500;
    }
  }
`;

const ReviewsSection = styled.div`
  color: white;
  
  .reviews-summary {
    background: rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
    border-radius: 0.75rem;
    margin-bottom: 2rem;
    
    .rating-overview {
      display: flex;
      align-items: center;
      gap: 2rem;
      margin-bottom: 1rem;
      
      .rating-number {
        font-size: 3rem;
        font-weight: 700;
        color: #3b82f6;
      }
      
      .rating-stars {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
    }
  }
  
  .review-form {
    background: rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
    border-radius: 0.75rem;
    margin-bottom: 2rem;
    
    h3 {
      margin-bottom: 1rem;
      color: #3b82f6;
    }
    
    .form-group {
      margin-bottom: 1rem;
      
      label {
        display: block;
        margin-bottom: 0.5rem;
        color: rgba(255, 255, 255, 0.8);
      }
      
      input, textarea, select {
        width: 100%;
        padding: 0.75rem;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 0.5rem;
        color: white;
        font-size: 1rem;
        
        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
        
        &:focus {
          outline: none;
          border-color: #3b82f6;
          background: rgba(255, 255, 255, 0.15);
        }
      }
      
      textarea {
        resize: vertical;
        min-height: 100px;
      }
      
      .user-info-display {
        background: rgba(59, 130, 246, 0.1);
        border: 1px solid rgba(59, 130, 246, 0.3);
        border-radius: 0.5rem;
        padding: 1rem;
        
        .user-info-text {
          display: block;
          font-size: 1rem;
          font-weight: 500;
          color: #3b82f6;
          margin-bottom: 0.25rem;
        }
        
        .user-info-hint {
          display: block;
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.6);
          font-style: italic;
        }
      }
    }
    
    .star-rating {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1rem;
      
      .star {
        font-size: 1.5rem;
        color: rgba(255, 255, 255, 0.3);
        cursor: pointer;
        transition: color 0.2s ease;
        
        &:hover {
          color: #fbbf24;
        }
        
        &.active {
          color: #fbbf24;
        }
      }
    }
  }
  
  .reviews-list {
    .review-item {
      background: rgba(255, 255, 255, 0.05);
      padding: 1.5rem;
      border-radius: 0.75rem;
      margin-bottom: 1rem;
      
      .review-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
        
        .reviewer-info {
          .reviewer-name {
            font-weight: 600;
            margin-bottom: 0.25rem;
          }
          
          .review-date {
            font-size: 0.9rem;
            color: rgba(255, 255, 255, 0.6);
          }
        }
        
        .review-rating {
          color: #fbbf24;
        }
      }
      
      .review-content {
        line-height: 1.6;
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
`;

function ProductPage() {
  const { id } = useParams();
  console.log('ID récupéré depuis URL:', id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const offerId = searchParams.get('offer');
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flashOffers, setFlashOffers] = useState([]);
  const [flashOffer, setFlashOffer] = useState(null);
  const [offerRestrictions, setOfferRestrictions] = useState({});
  
  const [configuration, setConfiguration] = useState({
    storage: '128GB',
    color: 'Noir'
  });

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('config');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  // Charger le produit depuis l'API
  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        console.log('Tentative de chargement du produit avec ID:', id);
        
        // Ajouter un timeout pour éviter le chargement infini
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 5000)
        );
        
        const productData = await Promise.race([
          productService.getProductById(id),
          timeoutPromise
        ]);
        
        console.log('Produit chargé:', productData);
        console.log('Images du produit:', productData.product_images);
        setProduct(productData);
        setError(null);
        setLoading(false);
        console.log('Loading set to false, product state updated');
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Produit non trouvé');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadProduct();
    }
  }, [id]);

  // Charger les offres flash depuis l'API
  useEffect(() => {
    const loadFlashOffers = async () => {
      try {
        const flashOffersData = await flashOfferService.getAllFlashOffers();
        setFlashOffers(flashOffersData);
      } catch (err) {
        console.error('Error loading flash offers:', err);
      }
    };

    loadFlashOffers();
  }, []);

  // Vérifier les restrictions d'offres pour les utilisateurs connectés
  useEffect(() => {
    const checkOfferRestrictions = async () => {
      if (!user || flashOffers.length === 0) return;
      
      try {
        // Convertir les offres flash au format attendu
        const formattedOffers = flashOffers.map(offer => ({
          id: offer.id,
          title: offer.title,
          discount: `-${offer.discount_percentage}%`,
          originalPrice: Number(offer.original_price),
          discountedPrice: Number(offer.discounted_price),
          description: offer.description,
          includes: offer.includes,
          icon: Clock,
          popular: false,
          stock: offer.stock,
          membership: 'flash',
          timeLeft: flashOfferService.getRemainingTime(offer.expires_at),
          badge: 'FLASH',
          product_slug: offer.product_slug
        }));
        
        // Vérifier les restrictions pour toutes les offres
        const restrictions = await offerRestrictionService.checkMultipleOffersAvailability(formattedOffers);
        
        // Convertir en objet pour un accès facile
        const restrictionsMap = {};
        restrictions.forEach(restriction => {
          restrictionsMap[restriction.offerId] = restriction;
        });
        
        setOfferRestrictions(restrictionsMap);
      } catch (error) {
        console.error('Error checking offer restrictions:', error);
      }
    };

    checkOfferRestrictions();
  }, [user, flashOffers]);

  useEffect(() => {
    if (offerId && flashOffers.find(offer => offer.id === offerId)) {
      const selectedFlashOffer = flashOffers.find(offer => offer.id === offerId);
      setFlashOffer(selectedFlashOffer);
    }
  }, [offerId, flashOffers]);
  
  // État pour le formulaire d'avis
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    rating: 5,
    title: '',
    comment: ''
  });
  
  // État pour les avis (API)
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [submittingReview, setSubmittingReview] = useState(false);

  // Pré-remplir le formulaire d'avis si l'utilisateur est connecté
  useEffect(() => {
    if (user) {
      const userName = user.first_name && user.last_name 
        ? `${user.first_name} ${user.last_name}` 
        : user.first_name || user.username || user.email?.split('@')[0] || '';
      
      console.log('Pré-remplissage avis - user:', user, 'userName:', userName);
      
      setReviewForm(prev => ({
        ...prev,
        name: userName,
        email: user.email || ''
      }));
    }
  }, [user]);
  const [displayedCount, setDisplayedCount] = useState(3);
  const [isExpanded, setIsExpanded] = useState(false);

  // Charger les avis depuis l'API
  useEffect(() => {
    const loadReviews = async () => {
      try {
        setReviewsLoading(true);
        const reviewsData = await reviewService.getReviews();
        // Filtrer les avis pour ce produit spécifique
        const productReviews = reviewsData.filter(review => 
          review.model.toLowerCase().includes(product?.name?.toLowerCase() || '')
        );
        // Mettre les avis les plus récents en tête (inverser l'ordre)
        setReviews(productReviews.reverse());
      } catch (error) {
        console.error('Erreur lors du chargement des avis:', error);
      } finally {
        setReviewsLoading(false);
      }
    };

    if (product) {
      loadReviews();
    }
  }, [product]);

  // Fonction pour générer le lien WhatsApp
  const generateWhatsAppLink = () => {
    const phoneNumber = '22812345678'; // Numéro WhatsApp du vendeur
    const message = `Bonjour! Je suis intéressé(e) par le ${product.name} (${configuration.storage}, ${configuration.color}). Pouvez-vous me donner plus d'informations?`;
    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  };

  // Gérer le changement d'onglet
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Fonctions pour la modal d'image
  const openImageModal = () => {
    setIsImageModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const navigateModalImage = (direction) => {
    const totalImages = 1 + (product?.product_images?.length || 0);
    
    if (direction === 'prev') {
      setSelectedImageIndex((prev) => prev === 0 ? totalImages - 1 : prev - 1);
    } else {
      setSelectedImageIndex((prev) => prev === totalImages - 1 ? 0 : prev + 1);
    }
  };

  const handleThumbnailClick = (index) => {
    setSelectedImageIndex(index);
  };

  const getCurrentModalImage = () => {
    if (selectedImageIndex === 0) {
      const url = product.image ? `https://inza-backend.sajholding.org${product.image}` : '/api/placeholder/800/800';
      console.log('Image principale - URL:', url);
      return url;
    } else {
      const imageData = product.product_images?.[selectedImageIndex - 1];
      const url = imageData?.image_url || imageData?.image ? 
        `https://inza-backend.sajholding.org${imageData.image}` : '/api/placeholder/800/800';
      console.log(`Image additionnelle ${selectedImageIndex}:`, imageData, 'URL:', url);
      return url;
    }
  };

  const getCurrentImageTitle = () => {
    if (selectedImageIndex === 0) {
      return `${product.name} - Vue principale`;
    } else {
      const imageData = product.product_images?.[selectedImageIndex - 1];
      return imageData?.alt_text || `${product.name} - Image ${selectedImageIndex}`;
    }
  };

  // Gérer la fermeture avec la touche Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isImageModalOpen) {
        closeImageModal();
      } else if (isImageModalOpen) {
        if (e.key === 'ArrowLeft') {
          navigateModalImage('prev');
        } else if (e.key === 'ArrowRight') {
          navigateModalImage('next');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen, selectedImageIndex, product]);

  // Utiliser le toast pour les messages
  const { addToast } = useToast();

  // Gérer la soumission du formulaire d'avis
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    // Validation : s'assurer que le nom n'est pas vide
    if (!reviewForm.name.trim()) {
      addToast('Le nom est obligatoire pour laisser un avis', { type: 'error' });
      setSubmittingReview(false);
      return;
    }
    
    console.log('Soumission avis - reviewForm:', reviewForm);
    
    setSubmittingReview(true);
    
    try {
      // Vérifier que le produit existe
      if (!product || !product.id) {
        throw new Error('Produit non valide');
      }
      
      // Préparer les données pour l'API
      const reviewData = {
        product: parseInt(product.id),
        name: reviewForm.name.trim(),
        email: reviewForm.email.trim(),
        rating: parseInt(reviewForm.rating),
        title: reviewForm.title.trim(),
        comment: reviewForm.comment.trim()
      };
      
      console.log('Données envoyées:', reviewData);
      
      // Appeler l'API pour créer l'avis
      const newReview = await reviewService.createReview(reviewData);
      
      // Ajouter l'avis localement (publié immédiatement)
      const tempReview = {
        id: Date.now(),
        author: reviewForm.name,
        avatar: reviewForm.name[0].toUpperCase(),
        rating: reviewForm.rating,
        date: 'À l\'instant',
        verified: !!user, // Vérifié si utilisateur connecté
        model: product.name,
        title: reviewForm.title,
        content: reviewForm.comment,
        helpful: 0,
        response: null
      };
      
      setReviews([tempReview, ...reviews]);
      
      // Réinitialiser le formulaire
      setReviewForm({
        name: '',
        email: '',
        rating: 5,
        title: '',
        comment: ''
      });
      
      // Afficher un message de succès
      addToast('Merci pour votre avis ! Il est maintenant visible.', {
        type: 'success',
        duration: 4000
      });
      
    } catch (error) {
      console.error('Erreur lors de la création de l\'avis:', error);
      
      // Gérer les erreurs spécifiques
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.error === 'Avis déjà existant') {
          addToast(errorData.detail || 'Vous avez déjà laissé un avis pour ce produit', { type: 'warning' });
        } else {
          addToast(errorData.detail || 'Erreur dans les données soumises', { type: 'error' });
        }
      } else {
        addToast('Erreur lors de la soumission de l\'avis. Veuillez réessayer.', { type: 'error' });
      }
    } finally {
      setSubmittingReview(false);
    }
  };
  // Gérer le changement de note
  const handleRatingChange = (rating) => {
    setReviewForm({...reviewForm, rating});
  };

  // Calculer la moyenne des avis
  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  // Gérer l'affichage des avis
  const displayedReviews = reviews.slice(0, displayedCount);

  // Basculer l'affichage des avis (afficher/masquer)
  const toggleReviews = () => {
    if (isExpanded) {
      // Masquer : revenir à 3 avis
      setDisplayedCount(3);
      setIsExpanded(false);
    } else {
      // Afficher : charger 3 avis supplémentaires ou tous
      const nextCount = Math.min(displayedCount + 3, reviews.length);
      setDisplayedCount(nextCount);
      if (nextCount >= reviews.length) {
        setIsExpanded(true);
      }
    }
  };

  // Contenu du bouton dynamique
  const getButtonText = () => {
    if (isExpanded) {
      return 'Masquer';
    }
    const remaining = reviews.length - displayedCount;
    const nextBatch = Math.min(3, remaining);
    if (nextBatch === 0) return 'Masquer';
    return `Voir plus (${nextBatch} avis${nextBatch > 1 ? 's' : ''} restants)`;
  };

  // Calculer le style pour chaque avis
  const getReviewStyle = (index) => {
    // Les avis affichés sont normaux
    if (index < displayedCount) {
      return {
        opacity: 1,
        transform: 'translateY(0)',
        filter: 'blur(0px)',
        scale: 1
      };
    }
    
    // Les avis non encore chargés sont estompés
    const distanceFromDisplayed = index - displayedCount + 1;
    const opacity = Math.max(0.1, 0.3 - distanceFromDisplayed * 0.05);
    const blurAmount = Math.min(1.5, distanceFromDisplayed * 0.3);
    
    return {
      opacity,
      transform: `translateY(${distanceFromDisplayed * 2}px) scale(0.95)`,
      filter: `blur(${blurAmount}px)`,
      pointerEvents: 'none'
    };
  };

  // Prix de base selon la configuration
  const getConfigurationPrice = () => {
    // Utiliser le prix réduit de l'offre flash si disponible, sinon le prix normal du produit
    let basePrice = 0;
    
    if (flashOffer && flashOffer.discounted_price) {
      // Utiliser le prix de l'offre flash depuis l'API
      basePrice = parseFloat(flashOffer.discounted_price) || 0;
    } else if (product && product.price) {
      // Utiliser le prix normal du produit
      basePrice = parseFloat(product.price) || 0;
    }
    
    // Trouver le prix du stockage sélectionné
    const selectedStorage = product?.storage_options?.find(s => s.name === configuration.storage);
    const storagePrice = selectedStorage ? (parseFloat(selectedStorage.price_surcharge) || 0) : 0;
    
    // Trouver le prix de la couleur sélectionnée
    const selectedColor = product?.colors?.find(c => c.name === configuration.color);
    const colorPrice = selectedColor ? (parseFloat(selectedColor.price_surcharge) || 0) : 0;
    
    const totalPrice = basePrice + storagePrice + colorPrice;
    
    // Debug: afficher les valeurs dans la console
    console.log('Price calculation:', {
      basePrice,
      storagePrice,
      colorPrice,
      totalPrice,
      flashOffer: flashOffer?.discounted_price,
      productPrice: product?.price
    });
    
    return totalPrice;
  };

  // Caractéristiques dynamiques selon configuration
  const getDynamicFeatures = () => {
    const features = [...product.features];
    
    // Ajouter features selon stockage
    if (configuration.storage === '512GB' || configuration.storage === '1TB') {
      features.push('Stockage ProMax');
    }
    
    // Ajouter features selon couleur
    if (configuration.color.includes('Titane')) {
      features.push('Finition Titane premium');
      features.push('Plus résistant aux rayures');
    }
    
    // Ajouter features selon offre flash
    if (flashOffer) {
      features.push('🔥 OFFRE FLASH EXCLUSIVE');
    }
    
    return features;
  };

  // Gérer les états de chargement et d'erreur
  console.log('Render state - loading:', loading, 'product:', product, 'error:', error);
  
  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h2>Chargement du produit...</h2>
          </div>
        </Container>
      </PageWrapper>
    );
  }

  if (error || !product) {
    return (
      <PageWrapper>
        <Container>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h2>Modèle introuvable</h2>
            <p>Découvrez tous les détails de ce modèle et préparez votre achat sur INZA STORE.</p>
            <p>Ce modèle n'a pas été trouvé.</p>
            <BackButton onClick={() => navigate(-1)}>
              ← Retour
            </BackButton>
          </div>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          ← Retour
        </BackButton>

        <ProductHeader>
          <h1>{product?.name || 'Modèle introuvable'}</h1>
          <p>
            Découvrez tous les détails de ce modèle et préparez votre achat sur INZA
            STORE.
          </p>
        </ProductHeader>

        {product ? (
          <div>
            <ProductLayout>
              {/* Galerie d'images */}
              <ProductGallery>
                {console.log('Rendu de la galerie - product:', product, 'images:', product?.product_images)}
                <div className="main-image" onClick={openImageModal}>
                  <img 
                    src={(() => {
                      let mainImageSrc;
                      if (selectedImageIndex === 0) {
                        mainImageSrc = product.image 
                          ? `https://inza-backend.sajholding.org${product.image}` 
                          : '/api/placeholder/400/400';
                      } else if (product.product_images && product.product_images[selectedImageIndex - 1]) {
                        const imageData = product.product_images[selectedImageIndex - 1];
                        mainImageSrc = imageData.image_url?.startsWith('http') 
                          ? imageData.image_url
                          : `https://inza-backend.sajholding.org${imageData.image_url || imageData.image}`;
                      } else {
                        mainImageSrc = '/api/placeholder/400/400';
                      }
                      console.log('Image principale - selectedImageIndex:', selectedImageIndex, 'src:', mainImageSrc);
                      return mainImageSrc;
                    })()}
                    alt={`${product.name} - Image ${selectedImageIndex + 1}`}
                  />
                </div>
                
                {/* Grille de thumbnails */}
                <div className="thumbnail-grid">
                  {/* Image principale */}
                  <div 
                    className={`thumbnail ${selectedImageIndex === 0 ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(0)}
                  >
                    <img 
                      src={product.image 
                        ? `https://inza-backend.sajholding.org${product.image}` 
                        : '/api/placeholder/80/80'} 
                      alt={`${product.name} - Vue principale`}
                    />
                  </div>
                  
                  {/* Images additionnelles */}
                  {console.log('Génération des thumbnails - nombre:', product.product_images?.length || 0)}
                  {product.product_images && product.product_images.map((img, index) => {
                    const thumbnailSrc = img.image_url?.startsWith('http') 
                      ? img.image_url
                      : `https://inza-backend.sajholding.org${img.image_url || img.image}`;
                    return (
                      <div 
                        key={index}
                        className={`thumbnail ${selectedImageIndex === index + 1 ? 'active' : ''}`}
                        onClick={() => handleThumbnailClick(index + 1)}
                      >
                        <img 
                          src={thumbnailSrc || '/api/placeholder/80/80'} 
                          alt={img.alt_text || `${product.name} - Image ${index + 2}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </ProductGallery>

              {/* Résumé produit */}
              <ProductSummary>
                <h2>{product.name}</h2>
                <div className="price">
                  {getConfigurationPrice().toLocaleString()} FCFA
                  {flashOffer && (
                    <div style={{ fontSize: '1rem', color: '#9ca3af', textDecoration: 'line-through' }}>
                      {(flashOffer.original_price ? 
                        parseFloat(flashOffer.original_price) : 
                        (product?.price ? parseFloat(product.price) : 0) + 
                        (product?.storage_options?.find(s => s.name === configuration.storage)?.price_surcharge || 0) +
                        (product?.colors?.find(c => c.name === configuration.color)?.price_surcharge || 0)
                      ).toLocaleString()} FCFA
                    </div>
                  )}
                </div>
                
                <div className="rating">
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map(star => (
                      <span key={star}>★</span>
                    ))}
                  </div>
                  <span>{product.rating} ({product.reviews} avis)</span>
                </div>
                
                <div className="availability">
                  ✓ En stock - Livraison 24-48h
                </div>

                {/* Actions rapides */}
                {user ? (
                  <div>
                    <AddToCartButton
                      onClick={() => {
                        const productData = {
                          id: product.id,
                          name: product.name,
                          price: getConfigurationPrice(),
                          emoji: '📱'
                        };
                        
                        addToCart(productData, configuration, flashOffer);
                      }}
                    >
                      🛒 Ajouter au panier
                    </AddToCartButton>
                    
                    <BuyButton
                      onClick={() => {
                        const orderParams = new URLSearchParams({
                          product: product.id,
                          storage: configuration.storage,
                          color: configuration.color,
                          ...(offerId && { offer: offerId })
                        });
                        navigate(`/checkout?${orderParams.toString()}`);
                      }}
                    >
                      {flashOffer ? 'Acheter avec l\'offre flash' : 'Continuer vers l\'achat'}
                    </BuyButton>
                    
                    <WhatsAppButton href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.065 2.876c1.396 2.394 2.987 3.372 4.613 4.238.612.306 1.095.489 1.47.626.617.196 1.18.169 1.624.103.496-.074 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      Contacter le vendeur
                    </WhatsAppButton>
                  </div>
                ) : (
                  <div>
                    <p style={{ color: '#f59e0b', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      🔐 Connectez-vous pour commander ce produit
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                      <BuyButton
                        onClick={() => navigate('/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search))}
                        style={{ background: '#3b82f6' }}
                      >
                        Se connecter
                      </BuyButton>
                      <BuyButton
                        onClick={() => navigate('/signup?redirect=' + encodeURIComponent(window.location.pathname + window.location.search))}
                        style={{ background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6' }}
                      >
                        Créer un compte
                      </BuyButton>
                      
                      <WhatsAppButton href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.065 2.876c1.396 2.394 2.987 3.372 4.613 4.238.612.306 1.095.489 1.47.626.617.196 1.18.169 1.624.103.496-.074 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        Contacter le vendeur
                      </WhatsAppButton>
                    </div>
                  </div>
                )}
              </ProductSummary>
            </ProductLayout>

            {/* Onglets d'information */}
            <TabsContainer>
              <div className="tabs-header">
                <button 
                  className={`tab-button ${activeTab === 'config' ? 'active' : ''}`}
                  onClick={() => handleTabChange('config')}
                >
                  Configuration
                </button>
                <button 
                  className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
                  onClick={() => handleTabChange('description')}
                >
                  Description & Caractéristiques
                </button>
                <button 
                  className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
                  onClick={() => handleTabChange('reviews')}
                >
                  Avis ({reviews.length})
                </button>
              </div>
              
              <div className="tab-content">
                {activeTab === 'config' && (
                  <div>
                    {/* Configuration et achat */}
                    <ConfigurationSection>
                      <h3>Stockage</h3>
                      <div className="config-options">
                        <div className="config-label">Choisissez votre capacité</div>
                        {product.storage_options && product.storage_options.map((storage) => {
                          return (
                            <ConfigOption 
                              key={storage.id}
                              className={configuration.storage === storage.name ? 'selected' : ''}
                              onClick={() => setConfiguration({...configuration, storage: storage.name})}
                            >
                              <div className="option-label">Stockage</div>
                              <div className="option-value">{storage.name}</div>
                              {storage.price_surcharge > 0 && (
                                <div className="price-diff">
                                  +{(storage.price_surcharge / 1000).toFixed(0)}k
                                </div>
                              )}
                            </ConfigOption>
                          );
                        })}
                      </div>
                    </ConfigurationSection>
                    
                    <ConfigurationSection>
                      <h3>Couleur</h3>
                      <div className="config-options">
                        <div className="config-label">Choisissez votre couleur</div>
                        {product.colors && product.colors.map((color) => {
                          return (
                            <ConfigOption 
                              key={color.id}
                              className={configuration.color === color.name ? 'selected' : ''}
                              onClick={() => setConfiguration({...configuration, color: color.name})}
                            >
                              <div className="option-label">Couleur</div>
                              <div className="option-value">{color.name}</div>
                              {color.price_surcharge > 0 && (
                                <div className="price-diff premium">
                                  +{(color.price_surcharge / 1000).toFixed(0)}k
                                </div>
                              )}
                            </ConfigOption>
                          );
                        })}
                      </div>
                    </ConfigurationSection>
                  </div>
                )}
                
                {activeTab === 'description' && (
                  <DescriptionSection>
                    <h3>Description</h3>
                    <p>
                      Découvrez le {product.name}, un produit exceptionnel qui combine design élégant, 
                      performances de pointe et technologies innovantes. Conçu pour les utilisateurs 
                      exigeants qui recherchent le meilleur en matière de qualité et de fiabilité.
                    </p>
                    
                    <h3>Caractéristiques principales</h3>
                    <div className="features-grid">
                      {(product.features || []).map((feature, index) => (
                        <div key={index} className="feature-item">
                          <div className="feature-icon">✓</div>
                          <div className="feature-title">{feature}</div>
                          <div className="feature-desc">Technologie de dernière génération</div>
                        </div>
                      ))}
                    </div>
                    
                    <SpecsTable>
                      <table>
                        <tbody>
                          <tr>
                            <th>Écran</th>
                            <td>{product.screenSize}</td>
                          </tr>
                          <tr>
                            <th>Processeur</th>
                            <td>{product.processor}</td>
                          </tr>
                          <tr>
                            <th>Caméra</th>
                            <td>{product.camera}</td>
                          </tr>
                          <tr>
                            <th>Vidéo</th>
                            <td>{product.video}</td>
                          </tr>
                          <tr>
                            <th>Batterie</th>
                            <td>{product.battery}</td>
                          </tr>
                          <tr>
                            <th>Poids</th>
                            <td>{product.weight}</td>
                          </tr>
                          <tr>
                            <th>Matériau</th>
                            <td>{product.material}</td>
                          </tr>
                          <tr>
                            <th>Résistance</th>
                            <td>{product.waterResistance}</td>
                          </tr>
                          <tr>
                            <th>Charge</th>
                            <td>{product.charging}</td>
                          </tr>
                          <tr>
                            <th>Affichage</th>
                            <td>{product.display}</td>
                          </tr>
                          {product.refreshRate !== 'N/A' && (
                            <tr>
                              <th>Taux de rafraîchissement</th>
                              <td>{product.refreshRate}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </SpecsTable>
                  </DescriptionSection>
                )}
                
                {activeTab === 'reviews' && (
                  <ReviewsSection>
                    <div className="reviews-summary">
                      <div className="rating-overview">
                        <div className="rating-number">{getAverageRating()}</div>
                        <div className="rating-stars">
                          <div className="stars">
                            {[1, 2, 3, 4, 5].map(star => (
                              <span key={star} className="star">★</span>
                            ))}
                          </div>
                          <div>{reviews.length} avis</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="review-form">
                      <h3>Laisser un avis</h3>
                      <form onSubmit={handleReviewSubmit}>
                        {user ? (
                          <>
                            <div className="form-group">
                              <label>Nom</label>
                              <div className="user-info-display">
                                <span className="user-info-text">
                                  {reviewForm.name || 'Nom non disponible'}
                                </span>
                                <small className="user-info-hint">Connecté en tant que {user.username || user.email}</small>
                              </div>
                            </div>
                            
                            <div className="form-group">
                              <label>Email</label>
                              <div className="user-info-display">
                                <span className="user-info-text">
                                  {reviewForm.email || 'Email non disponible'}
                                </span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="form-group">
                              <label>Nom</label>
                              <input
                                type="text"
                                value={reviewForm.name}
                                onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                                placeholder="Votre nom"
                                required
                              />
                            </div>
                            
                            <div className="form-group">
                              <label>Email</label>
                              <input
                                type="email"
                                value={reviewForm.email}
                                onChange={(e) => setReviewForm({...reviewForm, email: e.target.value})}
                                placeholder="votre@email.com"
                                required
                              />
                            </div>
                          </>
                        )}
                        
                        <div className="form-group">
                          <label>Note</label>
                          <div className="star-rating">
                            {[1, 2, 3, 4, 5].map(star => (
                              <span
                                key={star}
                                className={`star ${star <= reviewForm.rating ? 'active' : ''}`}
                                onClick={() => handleRatingChange(star)}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="form-group">
                          <label>Titre de l'avis</label>
                          <input
                            type="text"
                            value={reviewForm.title}
                            onChange={(e) => setReviewForm({...reviewForm, title: e.target.value})}
                            placeholder="Résumé de votre expérience"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Commentaire</label>
                          <textarea
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                            placeholder="Décrivez votre expérience avec ce produit..."
                            required
                          />
                        </div>
                        
                        <AddToCartButton type="submit" disabled={submittingReview}>
                          {submittingReview ? 'Envoi en cours...' : 'Soumettre mon avis'}
                        </AddToCartButton>
                      </form>
                    </div>
                    
                    <div className="reviews-list">
                      <h3>Avis des clients</h3>
                      {reviewsLoading ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <div>Chargement des avis...</div>
                        </div>
                      ) : reviews.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <div>Soyez le premier à donner votre avis sur ce produit !</div>
                        </div>
                      ) : (
                        <>
                          {reviews.map((review, index) => (
                        <div 
                          key={review.id} 
                          className="review-item"
                          style={{
                            ...getReviewStyle(index),
                            transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            pointerEvents: index >= displayedCount ? 'none' : 'auto',
                            position: 'relative',
                            zIndex: index < displayedCount ? 10 : Math.max(1, 10 - index)
                          }}
                        >
                          <div className="review-header">
                            <div className="reviewer-info">
                              <div className="reviewer-name">{review.name}</div>
                              <div className="review-date">{review.date}</div>
                            </div>
                            <div className="review-rating">
                              {[1, 2, 3, 4, 5].map(star => (
                                <span key={star} className={star <= review.rating ? 'active' : ''}>
                                  ★
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="review-content">
                            <h4>{review.title}</h4>
                            <p>{review.comment}</p>
                          </div>
                        </div>
                        ))}
                          
                          {/* Bouton Voir Plus/Masquer - après le 3ème avis */}
                          {reviews.length > 3 && (
                            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                              <button
                                onClick={toggleReviews}
                                style={{
                                  background: 'transparent',
                                  border: '1px solid rgba(255, 107, 53, 0.3)',
                                  color: '#FF6B35',
                                  padding: '12px 28px',
                                  borderRadius: '50px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  margin: '0 auto',
                                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                  backdropFilter: 'blur(10px)',
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.background = 'rgba(255, 107, 53, 0.1)';
                                  e.target.style.borderColor = '#FF6B35';
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.boxShadow = '0 8px 25px rgba(255, 107, 53, 0.15)';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = 'transparent';
                                  e.target.style.borderColor = 'rgba(255, 107, 53, 0.3)';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                                onMouseDown={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                }}
                              >
                                {getButtonText()}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </ReviewsSection>
                )}
              </div>
            </TabsContainer>
            
            {/* Offres flash associées */}
            <RelatedFlashOffers 
              currentProduct={product}
              flashOffers={flashOffers}
              offerRestrictions={offerRestrictions}
              isAuthenticated={!!user}
            />

            {/* Produits similaires */}
            <SimilarProducts 
              currentProduct={product}
              currentCategory={product.category_name}
            />
          </div>
        ) : (
          <p>Ce modèle n'a pas été trouvé.</p>
        )}
        
        {/* Modal d'image agrandie */}
        {isImageModalOpen && (
          <ImageModal onClick={closeImageModal}>
            <button className="close-button" onClick={closeImageModal}>
              <X size={24} />
            </button>
            
            <button 
              className="nav-button prev" 
              onClick={(e) => {
                e.stopPropagation();
                navigateModalImage('prev');
              }}
              disabled={selectedImageIndex === 0}
            >
              <ChevronLeft size={20} />
            </button>
            
            <img 
              src={getCurrentModalImage()}
              alt={getCurrentImageTitle()}
              className="modal-image"
              onClick={(e) => e.stopPropagation()}
            />
            
            <button 
              className="nav-button next" 
              onClick={(e) => {
                e.stopPropagation();
                navigateModalImage('next');
              }}
              disabled={selectedImageIndex === (product?.product_images?.length || 0)}
            >
              <ChevronRight size={20} />
            </button>
            
            <div className="image-info">
              <div className="image-title">{getCurrentImageTitle()}</div>
              <div className="image-counter">
                {selectedImageIndex + 1} / {1 + (product?.product_images?.length || 0)}
              </div>
            </div>
          </ImageModal>
        )}
      </Container>
    </PageWrapper>
  );
}

export default ProductPage; 
